import type {
  ApiSuccess,
  CountryCode,
  Gateway,
  InvoiceKind,
  PaymentIntentView,
  PaymentMethod,
  RefundDestination,
  RefundView,
  WalletView,
} from '@capsico/contracts';
import { api } from './client';

/**
 * Payments API (W11). The server re-checks every payment with the gateway — the app only hands
 * over what the gateway gave back, never decides that something was paid. Mutations carry an
 * X-Idempotency-Key (added by the client interceptor).
 */
export async function fetchWallet(country: CountryCode): Promise<WalletView> {
  const res = await api.get<ApiSuccess<WalletView>>('/v1/payments/me/wallet', {
    params: { country },
  });
  return res.data.data;
}

export async function fetchIntent(intentId: string): Promise<PaymentIntentView> {
  const res = await api.get<ApiSuccess<PaymentIntentView>>(`/v1/payments/me/intents/${intentId}`);
  return res.data.data;
}

export async function confirmIntent(
  intentId: string,
  params: Record<string, string>,
): Promise<PaymentIntentView> {
  const res = await api.post<ApiSuccess<PaymentIntentView>>(
    `/v1/payments/me/intents/${intentId}/confirm`,
    { params },
  );
  return res.data.data;
}

/** The gateway sent the customer back to /pay/return with these query parameters. */
export async function confirmReturn(
  gateway: Gateway,
  params: Record<string, string>,
): Promise<PaymentIntentView> {
  const res = await api.post<ApiSuccess<PaymentIntentView>>('/v1/payments/me/return', {
    gateway,
    params,
  });
  return res.data.data;
}

export async function retryPayment(
  orderId: string,
  method: PaymentMethod,
): Promise<PaymentIntentView> {
  const res = await api.post<ApiSuccess<PaymentIntentView>>(
    `/v1/payments/me/orders/${orderId}/retry`,
    { method },
  );
  return res.data.data;
}

export async function abandonOrder(orderId: string): Promise<{ cancelled: boolean }> {
  const res = await api.post<ApiSuccess<{ cancelled: boolean }>>(
    `/v1/payments/me/orders/${orderId}/abandon`,
    {},
  );
  return res.data.data;
}

export async function chooseRefund(
  refundId: string,
  destination: RefundDestination,
): Promise<RefundView> {
  const res = await api.post<ApiSuccess<RefundView>>(`/v1/payments/me/refunds/${refundId}/choice`, {
    destination,
  });
  return res.data.data;
}

/** A tax invoice PDF and the file name the server suggests. */
export async function downloadInvoice(
  orderId: string,
  kind: InvoiceKind,
): Promise<{ blob: Blob; fileName: string }> {
  const res = await api.get<Blob>(`/v1/payments/me/orders/${orderId}/invoices/${kind}`, {
    responseType: 'blob',
  });
  const header = String(res.headers['content-disposition'] ?? '');
  const match = /filename="?([^";]+)"?/i.exec(header);
  return { blob: res.data, fileName: match?.[1] ?? `capsico-${kind}-invoice.pdf` };
}
